import { useMemo, useState, type FormEvent } from "react";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, Field, Icon } from "@cntrl/ui";
import { useCreateTour } from "../../app/hooks/useShows.js";
import styles from "./TourBuilder.module.scss";

// The click-cycle (shot-44): each day cycles through these, and clicking past the last one CLEARS the day.
// A show/festival day becomes an INDEPENDENT show record under the tour; an off day becomes a `tour_day`.
//
// THREE steps, not four (PO, 2026-09-06: *"off/travel can be the same item"*). `travel` is still a real
// `tour_days.kind` — the API takes it, `OffDayModal` offers it as its own chip, and the Ops Calendar colours
// it separately — it is just not a distinction this PICKER asks for. Laying out a tour, the question is
// whether a date is a gig; which flavour of non-gig it is belongs to the day itself, where there is room to
// say so. So the builder writes `off` and a day is refined later, rather than making every user step through
// a fourth state to get back to empty.
const CYCLE = ["show", "festival", "off"] as const;
type Kind = (typeof CYCLE)[number];

/**
 * What each kind is CALLED — the legend's words, and the tile's accessible name.
 *
 * The tiles carry no visible label any more (PO, same round), so this is the only thing that names a day's
 * state to anyone not reading its colour. It rides `aria-label` for exactly that reason: a fill is not a
 * carrier on its own, and "grey square" is not an answer to what state a date is in.
 */
const LABELS: Record<Kind, string> = { show: "Show", festival: "Festival", off: "Off / travel" };

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * The tour builder — a month calendar whose days click-cycle Show → Festival → Off/travel → clear.
 * Show/Festival days create INDEPENDENT show records under the tour header (each settles individually — never
 * one monolithic multi-date row); Off days create tour_days. A single tour invite (not built into this dev
 * entry) then covers all dates in one RSVP. Money-free.
 */
export function TourBuilder({ onClose }: { onClose: () => void }) {
  const createTour = useCreateTour();
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [name, setName] = useState("");
  const [days, setDays] = useState<Record<string, Kind>>({});

  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = new Date(year, month, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  const selectedCount = Object.keys(days).length;
  const showCount = Object.values(days).filter((k) => k === "show" || k === "festival").length;

  function cycle(dateKey: string) {
    setDays((prev) => {
      const cur = prev[dateKey];
      const next = cur === undefined ? CYCLE[0] : CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length];
      // After the last kind, one more click clears the day (remove).
      const rest = { ...prev };
      if (cur === CYCLE[CYCLE.length - 1]) {
        delete rest[dateKey];
        return rest;
      }
      return { ...rest, [dateKey]: next };
    });
  }

  function stepMonth(delta: number) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || selectedCount === 0) return;
    const dayList = Object.entries(days)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, kind]) => ({ date, kind }));
    createTour.mutate(
      { name: name.trim(), days: dayList },
      { onSuccess: () => { setName(""); setDays({}); onClose(); } },
    );
  }

  return (
    <form className={styles.builder} onSubmit={onSubmit} aria-label="Tour builder">
      {/* The key to the tiles below. Filled colours with nothing naming them are a puzzle, and the reference
          puts this line in the same place for the same reason. */}
      <p className={styles.legend}>
        {CYCLE.map((kind) => (
          <span key={kind} className={styles.legendItem}>
            <span className={styles.legendSwatch} data-kind={kind} aria-hidden="true" />
            {LABELS[kind]}
          </span>
        ))}
      </p>

      <div className={styles.calHead}>
        <button type="button" className={styles.navBtn} onClick={() => stepMonth(-1)} aria-label="Previous month">
          <Icon icon={ChevronLeft} size={16} />
        </button>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <button type="button" className={styles.navBtn} onClick={() => stepMonth(1)} aria-label="Next month">
          <Icon icon={ChevronRight} size={16} />
        </button>
      </div>

      <div className={styles.grid} role="grid">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i} className={styles.dow} aria-hidden>{d}</span>
        ))}
        {Array.from({ length: firstWeekday }, (_, i) => <span key={`pad-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = iso(year, month, day);
          const kind = days[key];
          return (
            <button
              key={key}
              type="button"
              className={styles.cell}
              data-kind={kind ?? undefined}
              onClick={() => cycle(key)}
              aria-label={`${key}${kind ? `, ${LABELS[kind]}` : ""}`}
            >
              <span className={styles.dayNum}>{day}</span>
            </button>
          );
        })}
      </div>

      <p className={styles.hint}>
        Click a day to cycle Show → Festival → Off/travel → clear. {showCount} show{showCount === 1 ? "" : "s"} · {selectedCount} day{selectedCount === 1 ? "" : "s"}.
      </p>

      <div className={styles.footer}>
        <Field label="Tour name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className={styles.footerActions}>
          <Button type="submit" size="sm" loading={createTour.isPending} disabled={!name.trim() || selectedCount === 0}>
            <Icon icon={Plus} size={16} /> Create tour
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </form>
  );
}
